/**
 * CalGreen 5.408 Overview Page
 * Educational content about CalGreen requirements
 */

import { Link } from 'react-router-dom';

export function CalGreenOverview() {
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-8">
        <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
          ← Back to Home
        </Link>
      </div>

      <h1 className="text-4xl font-bold text-neutral-900 mb-4">
        CalGreen 5.408 Overview
      </h1>

      <p className="text-lg text-neutral-600 mb-8">
        Understanding California&apos;s construction waste diversion requirements
      </p>

      <div className="prose prose-neutral max-w-none">
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
            What is CalGreen 5.408?
          </h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            CalGreen Section 5.408 is part of the California Green Building Standards Code (Title 24, Part 11).
            It requires construction and demolition projects to divert at least 65% of their waste from landfills
            through recycling, reuse, salvage, or other approved methods.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 my-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">Key Requirement</h3>
            <p className="text-blue-800">
              Projects must divert a minimum of <strong>65% of construction and demolition debris</strong> by weight
              from landfills. This applies to most non-residential projects and residential projects over a certain size.
            </p>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
            Who Must Comply?
          </h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            CalGreen 5.408 typically applies to:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>New commercial construction projects</li>
            <li>Major commercial renovations</li>
            <li>Demolition of commercial buildings</li>
            <li>Residential projects over 1,000 square feet (varies by jurisdiction)</li>
            <li>Mixed-use development projects</li>
          </ul>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 my-6">
            <h3 className="text-lg font-semibold text-yellow-900 mb-2">Important Note</h3>
            <p className="text-yellow-800">
              Requirements can vary by local jurisdiction. Some cities and counties have stricter requirements
              or different thresholds. Always check with your local building department for specific requirements.
            </p>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
            What Materials Can Be Diverted?
          </h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            Common construction and demolition materials that can be diverted include:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4">
              <h4 className="font-semibold text-neutral-900 mb-2">High-Value Materials</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-neutral-700">
                <li>Metal (steel, aluminum, copper)</li>
                <li>Concrete and masonry</li>
                <li>Asphalt</li>
                <li>Clean wood and lumber</li>
              </ul>
            </div>
            <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4">
              <h4 className="font-semibold text-neutral-900 mb-2">Other Recyclables</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-neutral-700">
                <li>Drywall and gypsum board</li>
                <li>Cardboard and paper</li>
                <li>Plastic packaging</li>
                <li>Glass and glazing</li>
              </ul>
            </div>
            <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4">
              <h4 className="font-semibold text-neutral-900 mb-2">Reusable Items</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-neutral-700">
                <li>Doors and windows</li>
                <li>Fixtures and hardware</li>
                <li>Cabinetry</li>
                <li>Roofing materials</li>
              </ul>
            </div>
            <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4">
              <h4 className="font-semibold text-neutral-900 mb-2">Organic Materials</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-neutral-700">
                <li>Land clearing debris</li>
                <li>Wood chips and mulch</li>
                <li>Soil and vegetation</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
            Diversion Methods
          </h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            Materials can be diverted from landfills through several methods:
          </p>
          <div className="space-y-4">
            <div className="border-l-4 border-primary-500 pl-4">
              <h4 className="font-semibold text-neutral-900">Recycling</h4>
              <p className="text-neutral-700 text-sm">
                Processing materials to create new products (e.g., crushing concrete for aggregate)
              </p>
            </div>
            <div className="border-l-4 border-primary-500 pl-4">
              <h4 className="font-semibold text-neutral-900">Reuse</h4>
              <p className="text-neutral-700 text-sm">
                Using materials in their current form without processing (e.g., salvaging doors)
              </p>
            </div>
            <div className="border-l-4 border-primary-500 pl-4">
              <h4 className="font-semibold text-neutral-900">Salvage</h4>
              <p className="text-neutral-700 text-sm">
                Recovering materials for sale or donation to others
              </p>
            </div>
            <div className="border-l-4 border-primary-500 pl-4">
              <h4 className="font-semibold text-neutral-900">Composting</h4>
              <p className="text-neutral-700 text-sm">
                Converting organic materials into soil amendment
              </p>
            </div>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
            Documentation Requirements
          </h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            To demonstrate compliance, you must provide:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>A waste management plan (like those created with CalWMP)</li>
            <li>Estimated quantities of waste by material type</li>
            <li>Identification of diversion facilities and methods</li>
            <li>Final weight tickets or receipts from facilities</li>
            <li>Calculations showing 65% or greater diversion rate</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
            Enforcement and Penalties
          </h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            Non-compliance with CalGreen 5.408 can result in:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Delays in building permit approval</li>
            <li>Withheld certificates of occupancy</li>
            <li>Fines and penalties from local jurisdictions</li>
            <li>Required corrective action plans</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
            Tips for Success
          </h2>
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 my-6">
            <ul className="space-y-3 text-green-900">
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span><strong>Plan Early:</strong> Include waste diversion in project planning and budgeting</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span><strong>Source Separate:</strong> Keep materials separated on-site for easier recycling</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span><strong>Find Facilities:</strong> Research recycling facilities before starting work</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span><strong>Track Everything:</strong> Keep detailed records and weight tickets</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span><strong>Train Crews:</strong> Educate workers about waste separation requirements</span>
              </li>
            </ul>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
            Additional Resources
          </h2>
          <ul className="space-y-2 text-neutral-700">
            <li>
              <a
                href="https://www.dgs.ca.gov/BSC/Resources/Page-Content/Building-Standards-Commission-Resources-List-Folder/CALGreen"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 underline"
              >
                Official CALGreen Standards
              </a>
            </li>
            <li>
              <a
                href="https://www.calrecycle.ca.gov/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 underline"
              >
                CalRecycle - California Department of Resources Recycling and Recovery
              </a>
            </li>
            <li>
              <Link to="/docs/materials-guide" className="text-primary-600 hover:text-primary-700 underline">
                Materials Handling Guide
              </Link>
            </li>
            <li>
              <Link to="/docs/faq" className="text-primary-600 hover:text-primary-700 underline">
                Frequently Asked Questions
              </Link>
            </li>
          </ul>
        </section>
      </div>

      <div className="mt-12 pt-6 border-t border-neutral-200">
        <p className="text-sm text-neutral-600 italic">
          This information is provided for educational purposes only. Requirements may vary by jurisdiction.
          Always consult with your local building department and qualified professionals for compliance verification.
        </p>
      </div>
    </div>
  );
}